/**
 * Design Tokens Hook
 *
 * This hook manages the application of design tokens based on:
 * 1. Campaign/tenant data from the backend (dynamic)
 * 2. Pre-defined tenant CSS files (static)
 *
 * It bridges the gap between backend campaign colors and the design token system.
 */

import { useEffect } from "react";

// Map of tenant slugs to their CSS file imports
const TENANT_STYLES = {
  nos: () => import("./primitives/nos.css"),
  // Add more tenants here:
  // vodafone: () => import('./primitives/vodafone.css'),
  // edp: () => import('./primitives/edp.css'),
};

/**
 * Converts campaign data from backend to CSS custom properties
 * This allows dynamic tokens from the database to override static ones
 */


const campaignToCSSVariables = (campaign) => {
  if (!campaign) return {};

  // Safe accessors — legacy campaigns may have campaign.template = null or
  // campaign.template.appearence = null. No optional chaining (?.) — Node 10 /
  // CRA v2 Babel does not include the proposal plugin for it.
  var template   = campaign.template;
  var appearence = template && template.appearence;

  // Nothing to apply for campaigns without the new token structure
  if (!appearence) return {};

  var colors      = appearence.colors      || {};
  var colorScheme = appearence.colors_scheme;
  var fontFamily  = appearence.font_family;

  const vars = {
    "--color-brand-default":    colors["brand-default"]      || undefined,
    "--color-brand-hover":      colors["brand-hover"]        || undefined,
    "--color-brand-pressed":    colors["brand-pressed"]      || undefined,
    "--color-brand-light":      colors["brand-light"]        || undefined,

    "--color-background-page":      colors["background-page"]      || undefined,
    "--color-background-primary":   colors["background-primary"]   || undefined,
    "--color-background-secondary": colors["background-secondary"]  || undefined,

    "--color-text-primary":   colors["text-primary"]   || undefined,
    "--color-text-secondary": colors["text-secondary"]  || undefined,
    "--color-text-terciary":  colors["text-tertiary"]   || undefined,
  };

  // Font override — only when the backend provides a font and we have appearence
  // TODO: JNS - Ping LCG override is temporary; font should come from the backend.
  if (fontFamily) {
    const resolvedFont = colorScheme === "base_2026"
      ? "Ping LCG, sans-serif"
      : fontFamily;
    vars["--font-body"]         = resolvedFont;
    vars["--font-title"]        = resolvedFont;
    vars["--font-family-body"]  = resolvedFont;
    vars["--font-family-title"] = resolvedFont;
  }

  return vars;
};

/**
 * Hook to apply design tokens based on campaign
 *
 * @param {Object} campaign - Campaign object from Redux store
 * @param {string} tenantSlug - Optional tenant slug to load static tokens
 */
export const useDesignTokens = (campaign, tenantSlug = null) => {
  // Load static tenant CSS if slug matches a known tenant
  useEffect(() => {
    const loadTenantStyles = async () => {
      const slug =
        tenantSlug ||
        (campaign && campaign.tenant_slug) ||
        (campaign && campaign.slug);
      if (slug && TENANT_STYLES[slug]) {
        try {
          await TENANT_STYLES[slug]();
          document.documentElement.setAttribute("data-tenant", slug);
        } catch (error) {
          console.warn(`Failed to load tenant styles for: ${slug}`, error);
        }
      }
    };

    loadTenantStyles();
  }, [campaign && campaign.slug, campaign && campaign.tenant_slug, tenantSlug]);

  // Apply dynamic campaign colors as CSS variables
  useEffect(() => {
    if (!campaign) return;

    const cssVariables = campaignToCSSVariables(campaign);
    const root = document.documentElement;

    // Apply each variable to :root
    Object.entries(cssVariables).forEach(([property, value]) => {
      if (value) {
        root.style.setProperty(property, value);
      }
    });

    // Cleanup on unmount or campaign change
    return () => {
      Object.keys(cssVariables).forEach((property) => {
        root.style.removeProperty(property);
      });
    };
  }, [
    // No optional chaining — Node 10 / CRA v2 Babel lacks the plugin.
    // Explicit && guards achieve the same safe short-circuit.
    campaign && campaign.template && campaign.template.appearence && campaign.template.appearence.colors,
    campaign && campaign.template && campaign.template.appearence && campaign.template.appearence.font_family,
  ]);
};

/**
 * Utility to get a CSS variable value
 */
export const getTokenValue = (tokenName) => {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(tokenName)
    .trim();
};

/**
 * Utility to set a CSS variable value dynamically
 */
export const setTokenValue = (tokenName, value) => {
  document.documentElement.style.setProperty(tokenName, value);
};

export default useDesignTokens;
